import React, {useContext, useEffect, useState} from 'react';
import {Bars} from 'react-loader-spinner'
import {authContext} from '../Context/Authentication/authContext';
import {
  Grid,
  Box,
  InputLabel,
  MenuItem,
  FormControl,
  InputAdornment,
  OutlinedInput,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Autocomplete
} from '@mui/material';
import Select, {SelectChangeEvent} from "@mui/material/Select";
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './WhatIfAnalysis.scss'

import Chart from './Chart/Chart'
import MultiChart from './Chart/MultiChart'
import CompareChart from './Chart/CompareChart'
import {getQuarterFromDate} from "../Helpers/helpers";

import {INDICATORS, LAGS, whatIfAPICall, getMortgageRate} from './helper'

const WhatIfAnalysis = () => {
  const {state, dispatch}: any = useContext(authContext);
  const [cities, setCities] = useState([]);
  const [indicator, setIndicator] = useState(INDICATORS[0].value);
  const [lag, setLag] = useState(LAGS[2].value);
  const [oldSelection, setOldSelection] = useState({
    indicator: INDICATORS[0].value,
    percentage: state?.filters?.whatIfPercentage,
    lag: LAGS[2].value,
    selectedCity: {name: ''}
  });
  const [ppsfData, setPpsfData] = useState([]);
  const [hsData, setHsData] = useState([]);
  const [domData, setDomData] = useState([]);
  const [ppsfTrendData, setPpsfTrendData] = useState([]);
  const [hsTrendData, setHsTrendData] = useState([]);
  const [domTrendData, setDomTrendData] = useState([]);
  const [ppsfCompareData, setPpsfCompareData] = useState([]);
  const [hsCompareData, setHsCompareData] = useState([]);
  const [domCompareData, setDomCompareData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isError, serError] = useState(false);
  const [overallData, setOverallData] = useState({
    ppsf: 0,
    hs: 0,
    dom: 0
  });
  const [mortgageRate, setMortgageRate] = useState(0);
  const [chartAvailable, setChartAvailable] = useState(false);
  const [predictionDate, setPredictionDate] = useState('2023-Q3');


  useEffect(() => {
    getMortgageRateValue()
    const citiesInfo = state?.filters?.citiesInfo
    setCities(citiesInfo)
    const selectedState = state?.filters?.state
    if (Object.keys(state?.filters?.predictionTill).length) {
      const predictionQuarter = getQuarterFromDate(state?.filters?.predictionTill[selectedState.toLowerCase()].city)
      setPredictionDate(predictionQuarter)
    }
    let isCityChange = state?.filters?.changeCity
    // @ts-ignore
    let preSelectedCity = citiesInfo[0]
    if (isCityChange) {
      preSelectedCity = state?.filters?.whatIfSelectedCity
    }
    dispatch({
      type: 'APPLY_FILTER',
      payload: {
        ...state.filters,
        whatIfSelectedCity: preSelectedCity,
      },
    });
  }, [state?.filters?.state, state?.filters?.citiesInfo])


  useEffect(() => {
    if (state?.filters?.hitSubmit) {
      handleSubmit()
      dispatch({
        type: 'APPLY_FILTER',
        payload: {
          ...state.filters,
          hitSubmit: false,
        },
      });
    }

  }, [state?.filters?.hitSubmit])

  const getMortgageRateValue = async () => {
    const mRate = await getMortgageRate()
    setMortgageRate(mRate)
  }


  const handleIndicatorChange = (event: SelectChangeEvent) => {
    setIndicator(event.target.value as string)
  };

  const handleNumberChange = (e: any) => {
    dispatch({
      type: 'APPLY_FILTER',
      payload: {
        ...state.filters,
        whatIfPercentage: e.target.value
      },
    });
  };


  const handleCityChange = async (event: any, value: any) => {
    dispatch({
      type: 'APPLY_FILTER',
      payload: {
        ...state.filters,
        whatIfSelectedCity: value
      },
    });
  };

  const handleLagChange = (event: SelectChangeEvent) => {
    setLag(event.target.value as string)
  };

  const handleSubmit = async () => {
    dispatch({
      type: 'APPLY_FILTER',
      payload: {
        ...state.filters,
        whatIfSubmit: true,
        changeCity: false
      },
    });
    setLoading(true)
    serError(false)
    setOldSelection({
      indicator,
      percentage: state?.filters?.whatIfPercentage,
      lag,
      selectedCity: state?.filters?.whatIfSelectedCity
    })

    try {
      // @ts-ignore
      const selectedIndicator = INDICATORS.find((i: any) => i.value === indicator)
      // @ts-ignore
      const selectedLag = LAGS.find((l: any) => l.value === lag)
      // @ts-ignore
      const {
        overall,
        isChartAvailable,
        PPSFAdjustedData,
        HSAdjustedData,
        DOMAdjustedData,
        PPSFTimelineData,
        HSTimelineData,
        DOMTimelineData,
        PPSFGrowthData,
        HSGrowthData,
        DOMGrowthData
      } = await whatIfAPICall(state?.filters?.state, selectedIndicator?.key, state?.filters?.whatIfPercentage, state?.filters?.whatIfSelectedCity?.name, selectedLag?.key, 'City')
      setChartAvailable(isChartAvailable)
      setOverallData(overall)

      if (isChartAvailable) {
        setPpsfData(PPSFAdjustedData)
        setHsData(HSAdjustedData)
        setDomData(DOMAdjustedData)
        setPpsfTrendData(PPSFTimelineData)
        setHsTrendData(HSTimelineData)
        setDomTrendData(DOMTimelineData)
        setPpsfCompareData(PPSFGrowthData)
        setHsCompareData(HSGrowthData)
        setDomCompareData(DOMGrowthData)
      }
    } catch (e) {
      console.error(e)
      serError(true)
    }

    setLoading(false)
  }

  return (
    <div className='what-if-container'>
      <div className="what-if-container__row">
        <Box sx={{flexGrow: 1}}>
          <Grid container spacing={2}>
            <Grid item xs={2.5}>
              <FormControl sx={{m: 1}} fullWidth variant="standard">
                <InputLabel shrink id="demo-simple-select-label">
                  Select Indicator
                </InputLabel>
                <Select
                  onChange={handleIndicatorChange}
                  value={indicator}
                  size="small"
                >
                  {INDICATORS.map((data: any) => (
                    <MenuItem disabled={data.disable} key={data.key} value={data.value}>{data.value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={2.5}>
              <FormControl sx={{m: 1}} fullWidth variant="standard">
                <InputLabel shrink id="demo-simple-select-label">
                  Change in Mortgage Rates (US)
                </InputLabel>
                <OutlinedInput
                  type='number'
                  className='percentage-input'
                  id="outlined-start-adornment"
                  endAdornment={<InputAdornment position="end">%</InputAdornment>}
                  value={state?.filters?.whatIfPercentage}
                  onChange={handleNumberChange}
                />
              </FormControl>

            </Grid>
            <Grid item xs={2.5}>
              <FormControl sx={{m: 1}} fullWidth variant="standard">
                <InputLabel shrink id="demo-simple-select-label">
                  Select {'City'}
                </InputLabel>
                <Autocomplete
                  className='what-if-auto-select'
                  key='what-if-options'
                  id='what-if-metric-tags'
                  onChange={handleCityChange}
                  loading={isLoading}
                  loadingText={'Loading...'}
                  options={cities}
                  value={state?.filters?.whatIfSelectedCity || null}
                  getOptionLabel={(option: any) => option?.name || null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={'City'}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={2.5}>
              <FormControl sx={{m: 1}} fullWidth variant="standard">
                <InputLabel shrink id="demo-simple-select-label">
                  Select Lag
                </InputLabel>
                <Select
                  onChange={handleLagChange}
                  value={lag}
                  size="small"
                >
                  {LAGS.map((data: any) => (
                    <MenuItem key={data.key} value={data.value}>{data.value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={2}>
              <FormControl sx={{m: 1}} fullWidth variant="standard">
                <Button
                  className='run-analysis'
                  size="large"
                  endIcon={<SendIcon/>}
                  onClick={handleSubmit}
                >
                  Run Analysis
                </Button>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <div style={{marginTop: '1%'}}>
                <span style={{
                  fontWeight: 600,
                  color: '#f7b047'
                }}>Current Mortgage Rate({predictionDate}): {mortgageRate}</span>
              </div>
            </Grid>
          </Grid>
        </Box>
      </div>
      {
        isError
          ? (
            <>
              <div className='what-if-container__row'>
                <Box sx={{flexGrow: 1}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <div className='no-data'>
                        No data available for selected City: {state?.filters?.whatIfSelectedCity?.name}
                      </div>
                    </Grid>
                  </Grid>
                </Box>
              </div>
            </>
          )
          : (
            <>
              {
                state?.filters?.whatIfSubmit ? (
                  <>
                    {isLoading
                      ? <Bars height="400" ariaLabel="bars-loading" wrapperClass="spinner-progress-bar spinner-position"
                              visible={true}/>
                      : (
                        <>
                          <div className='what-if-container__row'>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <div className="what-if-container__sub_heading">
                                  {oldSelection.percentage}% Change in the {oldSelection.indicator} has the following
                                  projected
                                  impact
                                  after {oldSelection.lag.toLowerCase()}.
                                </div>
                              </Grid>
                            </Grid>
                          </div>
                          <div className='what-if-container__row' style={{marginTop: '4%'}}>
                            <Grid container spacing={{xs: 4, md: 3}}>
                              <Grid item xs={4}>
                                <span
                                  className='section-sub-heading'>Price Per Sq. Ft ({oldSelection.lag.toLowerCase().replace(' lag', '')})</span>
                                <br/>
                                <span className='section-heading'>{overallData.ppsf}%</span>
                              </Grid>
                              <Grid item xs={4}>
                                <span
                                  className='section-sub-heading'>Home Sales ({oldSelection.lag.toLowerCase().replace(' lag', '')})</span>
                                <br/>
                                <span className='section-heading'>{overallData.hs}%</span>
                              </Grid>
                              <Grid item xs={4}>
                                <span
                                  className='section-sub-heading'>Days On Market ({oldSelection.lag.toLowerCase().replace(' lag', '')})</span>
                                <br/>
                                <span className='section-heading'>{overallData.dom}%</span>
                              </Grid>
                            </Grid>
                          </div>
                          {
                            chartAvailable && (
                              <>
                                <div className='what-if-container__row' style={{marginTop: '4%'}}>
                                  <Grid container spacing={{xs: 4, md: 3}}>
                                    <Grid item xs={6}>
                                      <div className='accordion-heading'>{oldSelection?.selectedCity?.name} Adjusted:
                                      </div>
                                      <Accordion>
                                        <AccordionSummary
                                          expandIcon={<ExpandMoreIcon/>}
                                          aria-controls="ppsf-content"
                                          id="ppsf-panel-header"
                                        >
                                          <Typography>PPSF</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <Chart
                                            chartData={ppsfData}
                                            chartId={'ppsf-chart'}
                                            title='Adjusted PPSF Projection'
                                            isMulti
                                          />
                                        </AccordionDetails>
                                      </Accordion>
                                      <Accordion>
                                        <AccordionSummary
                                          expandIcon={<ExpandMoreIcon/>}
                                          aria-controls="hs-panel-content"
                                          id="hs-panel-header"
                                        >
                                          <Typography>Home Sales</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <Chart
                                            chartData={hsData}
                                            chartId={'hs-chart'}
                                            title='Adjusted HS Projection'
                                            isMulti
                                          />
                                        </AccordionDetails>
                                      </Accordion>
                                      <Accordion>
                                        <AccordionSummary
                                          expandIcon={<ExpandMoreIcon/>}
                                          aria-controls="dom-panel-content"
                                          id="dom-panel-header"
                                        >
                                          <Typography>Days On Market</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <Chart
                                            chartData={domData}
                                            chartId={'dom-chart'}
                                            title='Adjusted DOM Projection  '
                                            isMulti
                                          />
                                        </AccordionDetails>
                                      </Accordion>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <div className='accordion-heading'>{oldSelection?.selectedCity?.name} Time Series
                                        Component:
                                      </div>
                                      <Accordion>
                                        <Accordion>
                                          <AccordionSummary
                                            expandIcon={<ExpandMoreIcon/>}
                                            aria-controls="ppsf-treand-content"
                                            id="ppsf-trend-panel-header"
                                          >
                                            <Typography>PPSF</Typography>
                                          </AccordionSummary>
                                          <AccordionDetails>
                                            <MultiChart
                                              chartData={ppsfTrendData}
                                              chartId={'ppsf-chart'}
                                              title={oldSelection?.selectedCity?.name}
                                            />
                                          </AccordionDetails>
                                        </Accordion>
                                      </Accordion>
                                      <Accordion>
                                        <AccordionSummary
                                          expandIcon={<ExpandMoreIcon/>}
                                          aria-controls="hs-trend-content"
                                          id="hs-trend-panel-header"
                                        >
                                          <Typography>Home Sales</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <MultiChart
                                            chartData={hsTrendData}
                                            chartId={'hs-chart'}
                                            title={oldSelection?.selectedCity?.name}
                                          />
                                        </AccordionDetails>
                                      </Accordion>
                                      <Accordion>
                                        <AccordionSummary
                                          expandIcon={<ExpandMoreIcon/>}
                                          aria-controls="dom-trend-content"
                                          id="dom-trend-panel-header"
                                        >
                                          <Typography>Days On Market</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <MultiChart
                                            chartData={domTrendData}
                                            chartId={'dom-chart'}
                                            title={oldSelection?.selectedCity?.name}
                                          />
                                        </AccordionDetails>
                                      </Accordion>
                                    </Grid>
                                  </Grid>
                                </div>
                                <div className='what-if-container__row' style={{marginTop: '4%'}}>
                                  <Grid container spacing={{xs: 4, md: 3}}>
                                    <Grid item xs={12}>
                                      <div className='accordion-heading'>{oldSelection?.indicator} vs Targets
                                        Comparisons:
                                      </div>
                                      <Accordion>
                                        <AccordionSummary
                                          expandIcon={<ExpandMoreIcon/>}
                                          aria-controls="ppsf-compare-content"
                                          id="ppsf-compare-panel-header"
                                        >
                                          <Typography>PPSF Monthly Growth vs {oldSelection.indicator} Monthly
                                            Growth</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <CompareChart
                                            chartData={ppsfCompareData}
                                            chartId={'ppsf-compare-chart'}
                                            title={`PPSF Monthly Growth vs ${oldSelection.indicator} Growth`}
                                            indicators={[`${oldSelection.indicator} Change Monthly`, `${oldSelection.selectedCity?.name} PPSF Monthly Growth`]}
                                          />
                                        </AccordionDetails>
                                      </Accordion>
                                      <Accordion>
                                        <AccordionSummary
                                          expandIcon={<ExpandMoreIcon/>}
                                          aria-controls="hs-compare-content"
                                          id="hs-compare-panel-header"
                                        >
                                          <Typography>Home Sales Monthly Growth vs {oldSelection.indicator} Monthly
                                            Growth</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <CompareChart
                                            chartData={hsCompareData}
                                            chartId={'hs-compare-chart'}
                                            title={`Home Sales Monthly Growth vs ${oldSelection.indicator} Growth`}
                                            indicators={[`${oldSelection.indicator} Change Monthly`, `${oldSelection.selectedCity?.name} Home Sales Monthly Growth`]}
                                          />
                                        </AccordionDetails>
                                      </Accordion>
                                      <Accordion>
                                        <AccordionSummary
                                          expandIcon={<ExpandMoreIcon/>}
                                          aria-controls="dom-compare-content"
                                          id="dom-compare-panel-header"
                                        >
                                          <Typography>Days On Market Sales Monthly Growth
                                            vs {oldSelection.indicator} Monthly Growth</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <CompareChart
                                            chartData={domCompareData}
                                            chartId={'dom-compare-chart'}
                                            title={`Days On Market Sales Monthly Growth vs ${oldSelection.indicator} Growth`}
                                            indicators={[`${oldSelection.indicator} Change Monthly`, `${oldSelection.selectedCity?.name} Days On Market Monthly Growth`]}
                                          />
                                        </AccordionDetails>
                                      </Accordion>
                                    </Grid>

                                  </Grid>
                                </div>
                                <div className='what-if-container__row' style={{marginTop: '4%'}}/>
                              </>
                            )
                          }
                        </>
                      )}
                  </>
                ) : (
                  <>
                    <div className='what-if-container__row'>
                      <Box sx={{flexGrow: 1}}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <div className='no-data'>
                              Please select a scenario and hit "Run Analysis" to see the analysis.
                            </div>
                          </Grid>
                        </Grid>
                      </Box>
                    </div>
                  </>
                )
              }
            </>
          )
      }
    </div>

  )
}

export default WhatIfAnalysis;